/**
 * HeroBlock — capa principal do santinho.
 *
 * Props: photoUrl (foto candidato), coverUrl (background), name, role,
 * candidateNumber, ctaText, ctaUrl, layout ('centered' | 'split').
 */

/* eslint-disable react-refresh/only-export-components */

import { Hash } from 'lucide-react';
import type { BlockDefinition, BlockRenderProps } from './types';
import { BlockShell } from './BlockShell';

interface HeroProps {
  photoUrl?: string;
  coverUrl?: string;
  name?: string;
  role?: string;
  candidateNumber?: string;
  ctaText?: string;
  ctaUrl?: string;
  layout?: 'centered' | 'split';
  showNumber?: boolean;
}

function HeroRender({ block, ctx }: BlockRenderProps) {
  const { tenant } = ctx;
  const props = block.props as HeroProps;
  const name = props.name || tenant.name;
  const photoUrl = props.photoUrl || tenant.publicPage.content.candidatePhoto;
  const coverUrl = props.coverUrl || tenant.publicPage.content.coverPhoto;
  const number = props.candidateNumber || tenant.publicPage.content.candidateNumber;
  const layout = props.layout || 'centered';
  const theme = tenant.theme;

  const showNumber = props.showNumber !== false && !!number;

  if (layout === 'split') {
    return (
      <BlockShell block={block} ctx={ctx}>
        <div className="grid md:grid-cols-2 gap-8 items-center">
          {photoUrl ? (
            <div className="relative">
              <img
                src={photoUrl}
                alt={name}
                className="w-full aspect-square object-cover rounded-3xl shadow-2xl"
                style={{ boxShadow: `0 20px 60px ${theme.primaryColor}30` }}
              />
            </div>
          ) : (
            <div
              className="w-full aspect-square rounded-3xl flex items-center justify-center text-white text-9xl font-display font-bold"
              style={{ backgroundColor: theme.primaryColor }}
            >
              {name.charAt(0)}
            </div>
          )}
          <div>
            <h1
              className="font-display text-4xl md:text-6xl font-extrabold leading-[1.05] tracking-tight"
              style={{ color: theme.primaryColor }}
            >
              {name}
            </h1>
            {props.role && (
              <p className="mt-4 text-xl md:text-2xl text-ink-600 font-medium">{props.role}</p>
            )}
            {showNumber && (
              <div
                className="mt-6 inline-flex items-center gap-3 px-6 py-3 rounded-2xl text-white shadow-lg"
                style={{ backgroundColor: theme.primaryColor }}
              >
                <Hash className="h-6 w-6 opacity-70" />
                <span className="font-display text-4xl font-extrabold tracking-wider">{number}</span>
              </div>
            )}
            {props.ctaText && props.ctaUrl && (
              <a
                href={props.ctaUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-6 inline-flex items-center gap-2 px-6 py-3 rounded-xl font-semibold text-white shadow-md hover:opacity-90 transition-opacity"
                style={{ backgroundColor: theme.secondaryColor }}
              >
                {props.ctaText}
              </a>
            )}
          </div>
        </div>
      </BlockShell>
    );
  }

  // Centered layout (default)
  return (
    <section
      data-block-id={block.id}
      data-block-type={block.type}
      className="relative overflow-hidden text-center text-white"
      style={{
        background: coverUrl
          ? `linear-gradient(135deg, ${theme.primaryColor}cc, ${theme.secondaryColor}cc), url(${coverUrl}) center/cover`
          : `linear-gradient(135deg, ${theme.primaryColor}, ${theme.secondaryColor})`,
      }}
    >
      <div className="absolute inset-0 bg-black/20 pointer-events-none" />
      <div className="relative container mx-auto px-4 py-20 md:py-32 max-w-3xl">
        {photoUrl && (
          <img
            src={photoUrl}
            alt={name}
            className="w-32 h-32 md:w-48 md:h-48 mx-auto rounded-full object-cover border-4 border-white shadow-2xl mb-6"
          />
        )}
        <h1 className="font-display text-4xl md:text-6xl font-extrabold leading-[1.05] tracking-tight drop-shadow-lg">
          {name}
        </h1>
        {props.role && (
          <p className="mt-4 text-lg md:text-2xl text-white/90 font-medium">{props.role}</p>
        )}
        {showNumber && (
          <div className="mt-8 inline-flex items-center gap-3 px-6 py-3 rounded-2xl bg-white/20 backdrop-blur-md border border-white/30">
            <Hash className="h-6 w-6 opacity-70" />
            <span className="font-display text-4xl font-extrabold tracking-wider">{number}</span>
          </div>
        )}
        {props.ctaText && props.ctaUrl && (
          <a
            href={props.ctaUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-8 inline-flex items-center gap-2 px-8 py-4 rounded-xl font-semibold bg-white shadow-xl hover:scale-105 transition-transform"
            style={{ color: theme.primaryColor }}
          >
            {props.ctaText}
          </a>
        )}
      </div>
    </section>
  );
}

export const heroBlockDefinition: BlockDefinition<HeroProps> = {
  type: 'hero',
  label: 'Capa principal',
  description: 'Hero com foto, nome e número eleitoral',
  category: 'hero',
  multiple: false,
  // icon set in registry to avoid circular import
  icon: Hash,
  defaultProps: {
    layout: 'centered',
    showNumber: true,
  },
  Render: HeroRender,
};
